import { useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { toast } from 'sonner'

export interface UploadedFile {
  fileId: string
  filename: string
  viewLink: string
  downloadLink: string
  size: number
  type: string
}

export interface UploadProgress {
  isUploading: boolean
  progress: number
  error?: string
}

export const useExternalFileUpload = () => {
  const [uploadStatus, setUploadStatus] = useState<Record<string, UploadProgress>>({})

  const uploadToExternalService = async (
    file: File,
    attachmentType: string,
    clientId?: string
  ): Promise<UploadedFile | null> => {
    const key = `${attachmentType}_${Date.now()}`
    
    try {
      setUploadStatus(prev => ({
        ...prev,
        [key]: { isUploading: true, progress: 0 }
      }))

      // التحقق من حجم الملف
      const maxSize = 2 * 1024 * 1024 // 2MB
      if (file.size > maxSize) {
        throw new Error('حجم الملف يجب أن يكون أقل من 2 ميغابايت')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('filename', file.name)
      formData.append('attachmentType', attachmentType)
      if (clientId) formData.append('clientId', clientId)

      // استخدام Supabase Storage endpoint فقط
      const endpoint = 'upload-to-drive'

      setUploadStatus(prev => ({
        ...prev,
        [key]: { isUploading: true, progress: 50 }
      }))

      const { data, error } = await supabase.functions.invoke(endpoint, {
        body: formData,
      })

      if (error) {
        console.error('Upload error:', error)
        throw new Error(error.message || 'فشل في رفع الملف')
      }

      if (data?.requiresSetup) {
        throw new Error('يرجى إعداد مفاتيح API للخدمة المطلوبة')
      }

      if (!data?.success) {
        throw new Error(data?.error || 'فشل في رفع الملف')
      }

      setUploadStatus(prev => ({
        ...prev,
        [key]: { isUploading: true, progress: 100 }
      }))

      toast.success('تم رفع الملف بنجاح وضغطه تلقائياً ✅')

      return {
        fileId: data.fileId,
        filename: data.filename,
        viewLink: data.viewLink,
        downloadLink: data.downloadLink,
        size: data.size,
        type: data.type,
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'حدث خطأ أثناء رفع الملف'
      
      setUploadStatus(prev => ({
        ...prev,
        [key]: { 
          isUploading: false, 
          progress: 0,
          error: errorMessage 
        }
      }))

      toast.error(errorMessage)
      return null

    } finally {
      // إزالة حالة التحميل بعد 3 ثوان
      setTimeout(() => {
        setUploadStatus(prev => {
          const newStatus = { ...prev }
          delete newStatus[key]
          return newStatus
        })
      }, 3000)
    }
  }

  const getUploadStatus = (attachmentType: string): UploadProgress | null => {
    const statusKey = Object.keys(uploadStatus).find(key => 
      key.startsWith(`${attachmentType}_`)
    )
    return statusKey ? uploadStatus[statusKey] : null
  }

  const isUploading = (attachmentType: string): boolean => {
    const status = getUploadStatus(attachmentType)
    return status?.isUploading || false
  }

  return {
    uploadToExternalService,
    getUploadStatus,
    isUploading,
    uploadStatus,
  }
}